import { fm } from 'Components/FormatMessage';
import { TESSearchField, TESSearchFields } from '../definitions';
import { TResMedDeviceMode, TResMedDeviceType, TSleepApneaESSearchFields } from '../definitions/sleepApnea';
import { diagnosesByPlatform } from '../functions/constants/diagnoses';

// General

export const sleepApneaExtendedSearchFields = (searchFields?: TESSearchFields): TESSearchField[] => [
  {
    name: 'gender',
    type: 'Radio',
    options: ['male', 'female'],
  },
  {
    name: 'age',
    type: 'RangeSlider',
    min: 0,
    max: 100,
    showRangeText: true,
    marks: [{ value: 33 }, { value: 66 }],
  },
  {
    name: 'professionalDriving',
    type: 'CheckboxSingle',
    option: 'true',
    optionFormatter: () => fm('general.true'),
    showLabel: true,
  },
  {
    name: 'diagnosis',
    type: 'Checkbox',
    options: diagnosesByPlatform.sleepApnea,
  },
  {
    name: 'diagnosisDate',
    type: 'Custom',
  },
  {
    name: 'examinations',
    type: 'Custom',
  },
  {
    name: 'medication',
    type: 'Custom',
  },
  {
    name: 'substance',
    type: 'Custom',
  },
  {
    name: 'treatment',
    type: 'Custom',
  },
  {
    name: 'papDevice',
    type: 'Custom',
  },
  {
    name: 'papTreatmentResponse',
    type: 'Custom',
  },
  {
    name: 'papSettings',
    type: 'Custom',
    // TODO: This condition should probably be finished. Most likely should only be shown if a device type + mode combination that maps to 2PV, ASV or VAPS is selected.
    // condition:
    //   searchFields?.papDevice &&
    //   searchFields.papDevice !== 'noCropping'
    // ...
  },
  {
    name: 'latestBMI',
    type: 'RangeSlider',
    min: 0,
    max: 60,
    showRangeText: true,
    marks: [{ value: 33.33 }, { value: 66.66 }],
  },
  {
    name: 'preDiagnosisESS',
    type: 'RangeSlider',
    min: 0,
    max: 24,
    showRangeText: true,
    marks: [{ value: 8 }, { value: 16 }],
  },
  {
    name: 'latestESS',
    type: 'RangeSlider',
    min: 0,
    max: 24,
    showRangeText: true,
    marks: [{ value: 8 }, { value: 16 }],
  },
  {
    name: 'latestESSDelta',
    type: 'RangeSlider',
    min: -24,
    max: 24,
    showRangeText: true,
    marks: [{ value: -8 }, { value: 8 }],
  },
  {
    name: 'tirednessOrNearMissWhileDrivingDuringLast12Months',
    type: 'Custom',
  },
  {
    name: 'weightGain',
    type: 'Custom',
  },
  {
    name: 'myServiceInUse',
    type: 'Radio',
    options: ['yes', 'no'],
  },
  {
    name: 'includeDeceased',
    type: 'Custom',
  },
];

export const defaultSleepApneaSearchValues: TSleepApneaESSearchFields = {
  examinations: 'noCropping',
  professionalDriving: 'noCropping',
  treatment: 'noCropping',
  papDevice: 'noCropping',
  papTreatmentResponse: 'noCropping',
  papSettings: 'noCropping',
  latestBMI: 'noCropping',
  preDiagnosisESS: 'noCropping',
  latestESS: 'noCropping',
  latestESSDelta: 'noCropping',
  tirednessOrNearMissWhileDrivingDuringLast12Months: 'noCropping',
  weightGain: 'noCropping',
  deathDate: 'noCropping',
};

export const examinationFieldToUnitMap = new Map([
  ['cB-pH', ''],
  ['cB-pCO2', 'kPa'],
  ['cB-pO2', 'kPa'],
  ['cB-HCO3-st', 'mmol/l'],
  ['cB-BE', 'mmol/l'],
]);

// ResMed

export const resMedDeviceTypeToModesMap = new Map<TResMedDeviceType, TResMedDeviceMode[]>([
  ['AirSense 10 AutoSet', ['AutoSet', 'CPAP']],
  ['AirSense 10 AutoSet for Her', ['AutoSet', 'AutoSet for Her', 'CPAP']],
  ['AirSense 10 Elite', ['CPAP']],
  ['AirSense 11 AutoSet', ['AutoSet', 'AutoSet for Her', 'CPAP']],
  ['AirSense 11 Elite', ['CPAP']],
  ['AirCurve 10 CS PaceWave', ['ASV', 'ASVAuto', 'CPAP']],
  ['Lumis 150', ['CPAP', 'S', 'ST', 'T', 'PAC', 'iVAPS']],
  ['Lumis 100', ['CPAP', 'S', 'ST', 'T', 'PAC', 'iVAPS']],
]);

export const resMedTreatmentResponseFieldToUnitMap = new Map([
  ['averagePapDeviceUsage', 'hoursPerDay'],
  ['averageResidueAhi', 'perHour'],
  ['spikeResidueAhi', 'perHour'],
  ['averageCai', 'perHour'],
  ['averageMaskLeakage', 'litersPerMinute'],
]);

export const resMedSettingsFieldToRangeMap = new Map([
  ['ipap', [4, 25]],
  ['epap', [4, 16]],
  ['pressureSupport', [4, 21]],
  ['respiratoryRate', [8, 35]],
]);
